/**
 * F-153: pick the *Fill from a file* file from the HOST's own folders — the
 * desktop app's Local Files, the team's shared Google Drive folders — instead
 * of hunting for the same file in the OS dialog. Drawn inline under the
 * field (a second modal over the create dialog would fight it for Escape).
 *
 * Kola draws the list; the host lists and reads (core/host.ts
 * `HostFileSource`). Only the files an import can take are offered — the
 * folders also hold PDFs and Word files, which are counted, not shown. With
 * `onlySelected` (a Short's create form), only the files the team marked
 * Selected are offered, too.
 */
import { AGENT_MAX_ATTACHMENT_CHARS, type AgentAttachment } from "@kola/shared";
import { useEffect, useMemo, useRef, useState } from "react";

import type { HostFileBadge, HostFileEntry, HostFileFolder, HostFileSource } from "../../core/host.js";
import { iconFile, iconFolder } from "../../ui/icons.js";
import { agentAttachmentFromText, attachmentProblem, isAgentAttachmentFile } from "./attachments.js";

export interface HostFileBrowserProps {
  source: HostFileSource;
  /** `path` is the host's, kept so a Short can find its script again (F-155). */
  onPick: (attachment: AgentAttachment, path: string) => void;
  onClose: () => void;
  /** Offer only the files the host marks `selected`; the rest are counted. */
  onlySelected?: boolean;
}

/** How long typing rests before the host is asked to search. */
export const HOST_FILE_SEARCH_DELAY_MS = 250;

type Listing =
  | { status: "loading" }
  | { status: "ready"; entries: readonly HostFileEntry[] }
  | { status: "error"; error: string };

export function HostFileBrowser({ source, onPick, onClose, onlySelected = false }: HostFileBrowserProps) {
  const [roots, setRoots] = useState<readonly HostFileFolder[] | null>(null);
  const [notSetUp, setNotSetUp] = useState(0);
  const [rootsError, setRootsError] = useState<string | null>(null);
  /** The folders opened, outermost first; empty = the list of top-level folders. */
  const [trail, setTrail] = useState<readonly HostFileFolder[]>([]);
  const [listing, setListing] = useState<Listing>({ status: "loading" });
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Listing | null>(null);
  const [reading, setReading] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    void source.folders().then((result) => {
      if (!alive.current) return;
      setRoots(result.folders);
      setNotSetUp(result.notSetUp ?? 0);
      setRootsError(result.error ?? null);
      // One folder: open it — there is nothing to choose between.
      const only = result.folders.length === 1 ? result.folders[0] : undefined;
      if (only !== undefined) setTrail([only]);
    });
  }, [source]);

  const here = trail[trail.length - 1];
  useEffect(() => {
    if (here === undefined) return;
    let current = true;
    setListing({ status: "loading" });
    void source.list(here.path).then((result) => {
      if (!current) return;
      setListing(
        result.entries !== undefined
          ? { status: "ready", entries: result.entries }
          : { status: "error", error: result.error ?? "This folder could not be opened." },
      );
    });
    return () => {
      current = false;
    };
  }, [source, here]);

  const searching = query.trim() !== "" && source.search !== undefined;
  useEffect(() => {
    if (!searching) {
      setResults(null);
      return;
    }
    let current = true;
    setResults({ status: "loading" });
    const timer = window.setTimeout(() => {
      void source.search!(query.trim()).then((result) => {
        if (!current) return;
        setResults(
          result.entries !== undefined
            ? { status: "ready", entries: result.entries }
            : { status: "error", error: result.error ?? "The search did not finish." },
        );
      });
    }, HOST_FILE_SEARCH_DELAY_MS);
    return () => {
      current = false;
      window.clearTimeout(timer);
    };
  }, [source, query, searching]);

  const pickFile = async (entry: HostFileEntry) => {
    if (reading !== null) return;
    setProblem(null);
    // More bytes than the limit could ever be as UTF-8 text: refuse unread.
    if (entry.size > AGENT_MAX_ATTACHMENT_CHARS * 4) {
      setProblem(attachmentProblem("too-large", entry.name));
      return;
    }
    setReading(entry.path);
    const result = await source.readText(entry.path);
    if (!alive.current) return;
    setReading(null);
    if (result.text === undefined) {
      setProblem(result.error ?? `"${entry.name}" could not be read.`);
      return;
    }
    const outcome = agentAttachmentFromText(entry.name, result.text, "f1");
    if (outcome.ok) onPick(outcome.attachment, entry.path);
    else setProblem(attachmentProblem(outcome.reason, entry.name));
  };

  /** Open a folder — from the list (one level down) or a search hit (anywhere). */
  const openFolder = (entry: HostFileEntry) => {
    setQuery("");
    setProblem(null);
    setTrail(trailTo(entry, trail, roots ?? []));
  };

  const shown = searching ? results : here === undefined ? null : listing;

  return (
    <div className="host-files" role="group" aria-label={source.label}>
      <div className="host-files-bar">
        <nav className="host-files-crumbs" aria-label="Folder">
          <button
            type="button"
            className="host-files-crumb"
            disabled={trail.length === 0 && !searching}
            onClick={() => {
              setQuery("");
              setTrail([]);
            }}
          >
            {source.label}
          </button>
          {!searching &&
            trail.map((folder, i) => (
              <span key={folder.path} className="host-files-crumb-step">
                <span aria-hidden className="host-files-crumb-sep">
                  ›
                </span>
                <button
                  type="button"
                  className="host-files-crumb"
                  disabled={i === trail.length - 1}
                  onClick={() => setTrail(trail.slice(0, i + 1))}
                >
                  {folder.name}
                </button>
              </span>
            ))}
        </nav>
        {source.search !== undefined && (roots?.length ?? 0) > 0 && (
          <input
            type="search"
            className="text-input host-files-search"
            placeholder="Search"
            aria-label={`Search ${source.label}`}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        )}
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
          Close
        </button>
      </div>

      {shown === null ? (
        <RootList
          roots={roots}
          notSetUp={notSetUp}
          error={rootsError}
          label={source.label}
          onOpen={(folder) => setTrail([folder])}
        />
      ) : (
        <EntryList
          listing={shown}
          searching={searching}
          roots={roots ?? []}
          reading={reading}
          onlySelected={onlySelected}
          onOpenFolder={openFolder}
          onPickFile={(entry) => void pickFile(entry)}
        />
      )}

      {problem !== null && <p className="host-files-problem">{problem}</p>}
    </div>
  );
}

function RootList({
  roots,
  notSetUp,
  error,
  label,
  onOpen,
}: {
  roots: readonly HostFileFolder[] | null;
  notSetUp: number;
  error: string | null;
  label: string;
  onOpen: (folder: HostFileFolder) => void;
}) {
  if (roots === null) return <p className="host-files-note">Loading…</p>;
  const missing =
    notSetUp > 0
      ? `${notSetUp} shared ${notSetUp === 1 ? "folder isn't" : "folders aren't"} added on this computer yet — add ${notSetUp === 1 ? "it" : "them"} on the ${label} page.`
      : null;
  if (roots.length === 0) {
    return (
      <p className="host-files-note">
        {error ?? missing ?? `No ${label} folders are set up on this computer yet.`}
      </p>
    );
  }
  return (
    <>
      <ul className="host-files-list">
        {roots.map((folder) => (
          <li key={folder.path}>
            <button type="button" className="host-files-row" onClick={() => onOpen(folder)}>
              <span className="host-files-icon">{iconFolder}</span>
              <span className="host-files-name">{folder.name}</span>
              <Badge badge={folder.badge} />
            </button>
          </li>
        ))}
      </ul>
      {(error ?? missing) !== null && <p className="host-files-note">{error ?? missing}</p>}
    </>
  );
}

function EntryList({
  listing,
  searching,
  roots,
  reading,
  onlySelected,
  onOpenFolder,
  onPickFile,
}: {
  listing: Listing;
  searching: boolean;
  roots: readonly HostFileFolder[];
  reading: string | null;
  onlySelected: boolean;
  onOpenFolder: (entry: HostFileEntry) => void;
  onPickFile: (entry: HostFileEntry) => void;
}) {
  const { usable, skipped } = useMemo(
    () =>
      listing.status === "ready" ? usableEntries(listing.entries, onlySelected) : { usable: [], skipped: 0 },
    [listing, onlySelected],
  );
  if (listing.status === "loading") {
    return <p className="host-files-note">{searching ? "Searching…" : "Loading…"}</p>;
  }
  if (listing.status === "error") return <p className="host-files-note">{listing.error}</p>;
  return (
    <>
      {usable.length === 0 ? (
        <p className="host-files-note">
          {searching
            ? "Nothing matches."
            : onlySelected
              ? "No folders or Selected storyboard files here."
              : "No folders or storyboard files here."}
        </p>
      ) : (
        <ul className="host-files-list">
          {usable.map((entry) => (
            <li key={entry.path}>
              <button
                type="button"
                className="host-files-row"
                disabled={reading !== null}
                aria-busy={reading === entry.path}
                onClick={() => (entry.kind === "dir" ? onOpenFolder(entry) : onPickFile(entry))}
              >
                <span className="host-files-icon">{entry.kind === "dir" ? iconFolder : iconFile}</span>
                <span className="host-files-name">
                  {entry.name}
                  {searching && <span className="host-files-where">{whereIs(entry, roots)}</span>}
                </span>
                <Badge badge={entry.badge} />
                <span className="host-files-meta">
                  {reading === entry.path ? "Reading…" : entry.kind === "file" ? fileMeta(entry) : ""}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {skipped > 0 && (
        <p className="host-files-note">
          {skipped} other {skipped === 1 ? "file" : "files"} here can't fill a board — only{" "}
          {onlySelected ? "Selected .md, .csv, .tsv and .txt files can." : ".md, .csv, .tsv and .txt can."}
        </p>
      )}
    </>
  );
}

function Badge({ badge }: { badge: HostFileBadge | undefined }) {
  if (badge === undefined) return null;
  return (
    <span className={`host-files-badge host-files-badge-${badge.tone}`} title={badge.title}>
      {badge.label}
    </span>
  );
}

/**
 * Folders first, then the files an import can take (and, with
 * `onlySelected`, the team chose); the rest only counted.
 */
export function usableEntries(
  entries: readonly HostFileEntry[],
  onlySelected = false,
): {
  usable: HostFileEntry[];
  skipped: number;
} {
  const byName = (a: HostFileEntry, b: HostFileEntry) =>
    a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
  const dirs = entries.filter((e) => e.kind === "dir").sort(byName);
  const files = entries.filter((e) => e.kind === "file");
  const usable = files
    .filter((e) => isAgentAttachmentFile({ name: e.name }) && (!onlySelected || e.selected === true))
    .sort(byName);
  return { usable: [...dirs, ...usable], skipped: files.length - usable.length };
}

/** The top-level folder `path` sits in (the deepest, should two nest). */
function rootOf(path: string, roots: readonly HostFileFolder[]): HostFileFolder | undefined {
  let best: HostFileFolder | undefined;
  for (const root of roots) {
    if (relativeSegments(root.path, path) === null) continue;
    if (best === undefined || root.path.length > best.path.length) best = root;
  }
  return best;
}

/** `path`'s folder names below `root`, or null when it is not inside it. */
function relativeSegments(root: string, path: string): string[] | null {
  const trimmed = root.replace(/[\\/]+$/, "");
  if (path === trimmed) return [];
  const rest = path.slice(trimmed.length);
  if (!path.startsWith(trimmed) || !/^[\\/]/.test(rest)) return null;
  return rest.split(/[\\/]+/).filter(Boolean);
}

/**
 * The breadcrumb trail down to a folder: one level below the current folder
 * when it is in the list, every folder between when it came from a search.
 */
export function trailTo(
  entry: HostFileEntry,
  trail: readonly HostFileFolder[],
  roots: readonly HostFileFolder[],
): HostFileFolder[] {
  const root = rootOf(entry.path, roots);
  const segments = root === undefined ? null : relativeSegments(root.path, entry.path);
  if (root === undefined || segments === null) {
    return [...trail, { name: entry.name, path: entry.path }];
  }
  const separator = root.path.includes("\\") && !root.path.includes("/") ? "\\" : "/";
  const steps: HostFileFolder[] = [root];
  let path = root.path.replace(/[\\/]+$/, "");
  for (const segment of segments) {
    path = `${path}${separator}${segment}`;
    steps.push({ name: segment, path });
  }
  return steps;
}

/** A search hit's folder, as the reader would say it: "Team Wiki › Shorts". */
function whereIs(entry: HostFileEntry, roots: readonly HostFileFolder[]): string {
  const root = rootOf(entry.path, roots);
  if (root === undefined) return "";
  const segments = relativeSegments(root.path, entry.path) ?? [];
  return [root.name, ...segments.slice(0, -1)].join(" › ");
}

function fileMeta(entry: HostFileEntry): string {
  const size =
    entry.size < 1024
      ? `${entry.size} B`
      : entry.size < 1024 * 1024
        ? `${Math.round(entry.size / 1024)} KB`
        : `${(entry.size / 1024 / 1024).toFixed(1)} MB`;
  if (entry.mtimeMs <= 0) return size;
  const date = new Date(entry.mtimeMs).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `${size} · ${date}`;
}
